import { isFlowExperience } from "@presentation/experience/utils"
import { Zone, partners, SubZone } from "@libs/datalayer/constants/datalayer.constants"
import { datalayer } from "@libs/datalayer/datalayer"
import { getPage } from "@libs/utils/urls"
import LocalStorageRepository from "@infra/repositories/LocalStorageRepository"
import { getBaseStorage } from "@context/TransactionStateContext"

import type { ModalAction } from "./error-catalog"

/**
 * Determina la zona (SOAT vs SOATExperimentación) basada en la URL.
 * Si la URL contiene "/experiencia", retorna SOATExperimentación, sino SOAT.
 */
export function getZone(): string {
  return isFlowExperience() ? Zone.SoatExperience : Zone.Soat
}

/**
 * Determina el partner basado en si está en modo experimentación.
 * Si está en experimentación, retorna "Experiencia", sino retorna el partner actual.
 */
export function getPartner(): string {
  if (isFlowExperience()) {
    return partners.experiencia
  }
  // Obtener el partner actual de localStorage
  return LocalStorageRepository.get("partnerName") || partners.pacifico
}

/**
 * Obtiene el ID de solicitud desde LocalStorage o del contexto de transacción.
 * Intenta primero LocalStorage["requestId"], si no está disponible, extrae de transactionBase.
 */
export function getRequestId(): string {
  const baseStorage = getBaseStorage()

  const requestIdFromQuoteResponse = baseStorage?.quoteResponse?.solicitudId
  if (requestIdFromQuoteResponse) {
    return requestIdFromQuoteResponse
  }

  // Fallback: obtener de transactionBase (almacenado en LocalStorage como JSON)
  try {
    return baseStorage?.requestResponse?.id || ""
  } catch {
    // Si hay error al parsear, retorna string vacío
    return ""
  }
}

/**
 * Mapea un ModalAction a su etiqueta descriptiva para tracking.
 * - tel → "Habla con un asesor"
 * - external → "Chatea con un asesor"
 * - close, callback, redirect, reload → "Entendido"
 */
export function getActionLabel(action: ModalAction): string {
  switch (action.kind) {
    case "tel":
      return "Habla con un asesor"
    case "external":
      return "Chatea con un asesor"
    case "close":
      return "Cerrar"
    case "callback":
    case "redirect":
    case "reload":
      return "Entendido"
    default:
      return "Entendido"
  }
}

/**
 * Obtiene la subZona (página actual) desde la URL.
 */
export function getSubZone(): string {
  if (typeof window !== "undefined") {
    return getPage(window.location.pathname) || ""
  }
  return ""
}

/**
 * Construye el payload completo para el evento "visualizar" (al abrir el modal).
 * 
 * valor: "ID_SOLICITUD – ACCIÓN – CÓDIGO_ERROR – MENSAJE_BACKEND – MENSAJE_MODAL"
 */
export interface VisualizarPayload {
  event: string
  partner: string
  partnerType: string
  tipoZona: string
  zona: string
  subZona: string
  seccion: string
  tipoEvento: string
  tipoElemento: string
  valor: string
}

export function buildVisualizarPayload({
  code,
  service,
  backendMessage,
  modalDescription,
}: {
  code?: string
  service?: string
  backendMessage?: string
  modalDescription?: string
}): VisualizarPayload {
  const requestId = getRequestId()
  const subZona = getSubZone()
  
  // Construir el campo "valor" con formato:
  // ID_SOLICITUD – ACCIÓN – CÓDIGO_ERROR – MENSAJE_BACKEND – MENSAJE_MODAL
  const valorParts = [
    requestId,
    subZona,
    code,
    backendMessage,
    typeof modalDescription === "string" ? modalDescription : "",
  ].filter(Boolean)
  
  const valor = valorParts.join(" – ") || "Error genérico"

  return {
    event: "virtualEvent",
    partner: getPartner(),
    partnerType: LocalStorageRepository.get("partnerType") || "Pacífico",
    tipoZona: "SOAT",
    zona: getZone(),
    subZona,
    seccion: "Modal – Error",
    tipoEvento: "visualizar",
    tipoElemento: "modal",
    valor,
  }
}

/**
 * Construye el payload completo para el evento "click" (interacción con botón).
 * 
 * valor: "ID_SOLICITUD – ACCIÓN – CÓDIGO_ERROR – TEXTO_BOTÓN"
 */
export interface ClickPayload {
  event: string
  partner: string
  partnerType: string
  tipoZona: string
  zona: string
  subZona: string
  seccion: string
  tipoEvento: string
  tipoElemento: string
  valor: string
}

export function buildClickPayload({
  code,
  action,
  actionLabel,
}: {
  code?: string
  action?: ModalAction
  actionLabel?: string
}): ClickPayload {
  const requestId = getRequestId()
  const subZona = getSubZone()
  const label = actionLabel || (action ? getActionLabel(action) : "Entendido")

  // Construir el campo "valor" con formato:
  // ID_SOLICITUD – ACCIÓN – CÓDIGO_ERROR – TEXTO_BOTÓN
  const valorParts = [
    requestId,
    subZona,
    code,
    label,
  ].filter(Boolean)
  
  const valor = valorParts.join(" – ") || "Entendido"

  return {
    event: "virtualEvent",
    partner: getPartner(),
    partnerType: LocalStorageRepository.get("partnerType") || "Pacífico",
    tipoZona: "SOAT",
    zona: getZone(),
    subZona,
    seccion: "Modal – Error",
    tipoEvento: "click",
    tipoElemento: "botón",
    valor,
  }
}

/**
 * Trackea el evento "visualizar" al abrir el modal.
 */
export function trackErrorModalVisualization({
  code,
  service,
  backendMessage,
  modalDescription,
}: {
  code?: string
  service?: string
  backendMessage?: string
  modalDescription?: string
}): void {
  const payload = buildVisualizarPayload({
    code,
    service,
    backendMessage,
    modalDescription,
  })
  
  datalayer.push(payload)
}

/**
 * Trackea el evento "click" cuando el usuario interactúa con un botón del modal.
 */
export function trackErrorModalClick({
  code,
  action,
  actionLabel,
}: {
  code?: string
  action?: ModalAction
  actionLabel?: string
}): void {
  const payload = buildClickPayload({
    code,
    action,
    actionLabel,
  })
  
  datalayer.push(payload)
}
